import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { withTracker } from 'meteor/react-meteor-data';
import { Meteor } from 'meteor/meteor';
import { Bert } from 'meteor/themeteorchef:bert';
import { isEmpty, isNaN } from 'lodash';
import { Autocomplete, Box, Button, FormControl, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import Layers from '../../../api/Layers/Layers';
import Loading from '../Loading';
import loadGeoJSONFromURL from '../../../modules/load-geojson-from-url';

const initialCode = `from pyodide.http import pyfetch
import geopandas as gpd
import matplotlib.pyplot as plt
from shapely.geometry import box
from io import BytesIO
import base64
import json
def convex_hull(gdf):
    return gdf.convex_hull

def concave_hull(gdf):
    return gdf  # Placeholder, implementar si es necesario

def buffer(gdf, size=1.0):
    return gdf.buffer(size)

def explode(gdf):
    return gdf.explode()
    
def simplify(gdf, tolerance):
    return gdf.simplify(tolerance)

def clip(gdf, bbox_str='0,0,10,10'):
    minx, miny, maxx, maxy = map(float, bbox_str.split(','))
    bbox_polygon = box(minx, miny, maxx, maxy)
    return gdf.clip(bbox_polygon)

def dissolve(gdf, by=None):
    return gdf.dissolve(by=by)

def overlay(gdf1, gdf2, how='intersection'):
    return gpd.overlay(gdf1, gdf2, how=how)

def sjoin(gdf1, gdf2, how='inner', op='intersects'):
    return gpd.sjoin(gdf1, gdf2, how=how, predicate=op)

def difference(gdf, other):
    return gdf.difference(other)

def intersection(gdf, other):
    return gdf.intersection(other)

def symmetric_difference(gdf, other):
    return gdf.symmetric_difference(other)

def union(gdf):
    return gdf.unary_union

def contains(gdf, other):
    return gdf.contains(other)

def within(gdf, other):
    return gdf.within(other)

def touches(gdf, other):
    return gdf.touches(other)

def crosses(gdf, other):
    return gdf.crosses(other)

def overlaps(gdf, other):
    return gdf.overlaps(other)

def intersects(gdf, other):
    return gdf.intersects(other)

def is_valid(gdf):
    return gdf.is_valid

def is_simple(gdf):
    return gdf.is_simple

# Funciones avanzadas

def dissolve(gdf, by=None):
    return gdf.dissolve(by=by)

def explode(gdf):
    return gdf.explode()

def clip(gdf, bbox_str='0,0,10,10'):
    minx, miny, maxx, maxy = map(float, bbox_str.split(','))
    bbox_polygon = box(minx, miny, maxx, maxy)
    return gdf.clip(bbox_polygon)

def overlay(gdf1, gdf2, how='intersection'):
    return gpd.overlay(gdf1, gdf2, how=how)

def sjoin(gdf1, gdf2, how='inner', op='intersects'):
    return gpd.sjoin(gdf1, gdf2, how=how, predicate=op)

def mapa(gdf):
    fig, ax = plt.subplots(figsize=(10, 6))
    gdf.plot(ax=ax, color='skyblue', edgecolor='black')
    ax.set_title('Mapa generado a partir del GeoJSON')

    buffer = BytesIO()
    plt.savefig(buffer, format='png')
    plt.close(fig)
    buffer.seek(0)

    plot_base64 = base64.b64encode(buffer.read()).decode('utf-8')
    return plot_base64

async def reader(data):
    if isinstance(data, str):
      data = json.loads(data)
    elif hasattr(data, "to_py"):
      data = data.to_py()
    gdf = gpd.GeoDataFrame.from_features(data["features"])  
    return gdf

async def main():
    gdf1 = await reader(geoJsonUrl1)
    gdf2 = await reader(geoJsonUrl2)
    
    gdf = convex_hull(gdf1)
    gdf = concave_hull(gdf)
    gdf = buffer(gdf, buffer_size)
    
    gdf = explode(gdf)
    gdf = clip(gdf, bbox)
    gdf = simplify(gdf, simplify_tolerance)
    plot_base64 = mapa(gdf)
    return plot_base64

plot_base64 = await main()
plot_base64`;

const PythonComponent = ({ layerList, loadingWithTracker, pythonSettings }) => {
  if (loadingWithTracker) return <Loading />;
  const { layerIDs, showChangeLayer } = pythonSettings;
  const { t } = useTranslation();
  const [pyodide, setPyodide] = useState(null);
  const [geoJsonUrl1, setGeoJsonUrl1] = useState(''); // layer --> hacer la llamada para sacar la geometría
  const [geoJsonUrl2, setGeoJsonUrl2] = useState('');
  const [loading, setLoading] = useState(false);
  const [plotUrl, setPlotUrl] = useState(null);
  const [bufferSize, setBufferSize] = useState(0.4); // configurar en mapeditor
  const [simplifyTolerance, setSimplifyTolerance] = useState(0.01); // configurar en mapeditor
  const [bbox, setBbox] = useState('0,0,10,10'); // configurar en mapeditor
  const [pythonCode, setPythonCode] = useState(initialCode);
  const [layerIDSelected, setLayerIDSelected] = React.useState(layerIDs);

  const validateBbox = bboxProp => {
    const parts = bboxProp.split(',');
    return parts.length === 4 && parts.every(p => !isNaN(parseFloat(p)));
  };

  const runQuery = async () => {
    if (!isEmpty(bbox) && !validateBbox(bbox)) {
      Bert.alert(
        'El BBOX debe tener el formato "minx, miny, maxx, maxy" con valores numéricos. Usando BBOX por defecto.',
        'warning'
      );
      setBbox('0,0,10,10');
    }

    if (pyodide) {
      setLoading(true);
      try {
        pyodide.globals.set('geoJsonUrl1', geoJsonUrl1);
        pyodide.globals.set('geoJsonUrl2', geoJsonUrl2);
        pyodide.globals.set('buffer_size', bufferSize);
        pyodide.globals.set('simplify_tolerance', simplifyTolerance);
        pyodide.globals.set('bbox', bbox);

        const plotBase64 = await pyodide.runPythonAsync(pythonCode);

        setPlotUrl(`data:image/png;base64,${plotBase64}`);
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    } else {
      console.error('Pyodide no está cargado');
    }
  };

  const handleChangeLayer = async layerSelected => {
    const geojsonFile1 = await loadGeoJSONFromURL(layerSelected[0].url);
    setGeoJsonUrl1(geojsonFile1);
    const newBBox =
      !isEmpty(layerSelected[0].mapLocation.bbox) &&
      layerSelected[0].mapLocation.bbox.map(value => Math.round(Number(value))).join(',');
    setBbox(newBBox);
    const geojsonFile2 = await loadGeoJSONFromURL(layerSelected[1].url);
    setGeoJsonUrl2(geojsonFile2);
  };

  useEffect(() => {
    async function initializePyodide() {
      try {
        const script = document.createElement('script');
        script.src = 'https://cdn.jsdelivr.net/pyodide/v0.27.0/full/pyodide.js';
        script.async = true;

        script.onload = async () => {
          const pyodideInstance = await window.loadPyodide({
            indexURL: 'https://cdn.jsdelivr.net/pyodide/v0.27.0/full/'
          });
          await pyodideInstance.loadPackage(['pandas', 'geopandas', 'matplotlib']);
          setPyodide(pyodideInstance);
          setLoading(false);
        };

        script.onerror = () => {
          Bert.alert(`${t('alert_loading_pyodide')}`, 'warning');
          setLoading(false);
        };

        document.body.appendChild(script);
      } catch (error) {
        console.error('Error al inicializar Pyodide:', error);
        setLoading(false);
      }
    }

    const initializeGeometries = async () => {
      if (showChangeLayer === true) {
        const layerSelected = layerList.filter(layer => layerIDs.includes(layer._id));
        handleChangeLayer(layerSelected);
      } else {
        handleChangeLayer(layerList);
      }
    };

    initializePyodide();
    initializeGeometries();
  }, []);

  const textLoading = loading ? `${t('processing')}` : `${t('run_code')}`;
  const textButtton = pyodide ? textLoading : `${t('loading_pyodide')}`;

  return (
    <Box sx={{ p: 2 }}>
      {showChangeLayer && showChangeLayer === true && (
        <FormControl fullWidth sx={{ mb: 2 }}>
          <Autocomplete
            multiple
            limitTags={2}
            options={layerList || []}
            getOptionLabel={option => option.name || ''}
            filterOptions={(options, { inputValue }) =>
              options.filter(option => option.name.toLowerCase().includes(inputValue.toLowerCase()))
            }
            value={layerList.filter(layer => layerIDSelected.includes(layer._id))}
            onChange={(event, newValue) => {
              if (newValue.length <= 2) {
                const newIDs = newValue.map(layer => layer._id);
                setLayerIDSelected(newIDs);
                const layerSelected = layerList.filter(layer => newIDs.includes(layer._id));
                handleChangeLayer(layerSelected);
              }
            }}
            isOptionEqualToValue={(option, value) => option._id === value._id}
            size="small"
            renderInput={params => <TextField {...params} label="Selecciona 2 capas" />}
            renderOption={(props, option) => (
              <li {...props} key={option._id}>
                {option.name}
              </li>
            )}
            disableCloseOnSelect
          />
        </FormControl>
      )}
      <Box sx={{ mb: 1 }}>
        <TextField
          fullWidth
          label="Tamaño del buffer"
          type="number"
          variant="outlined"
          value={bufferSize}
          onChange={e => setBufferSize(parseFloat(e.target.value))}
          margin="normal"
          size="small"
        />
      </Box>
      <Box sx={{ mb: 1 }}>
        <TextField
          fullWidth
          label="Tolerancia de simplificación"
          type="number"
          variant="outlined"
          value={simplifyTolerance}
          onChange={e => setSimplifyTolerance(parseFloat(e.target.value))}
          margin="normal"
          size="small"
        />
      </Box>
      <Box sx={{ mb: 1 }}>
        <TextField
          fullWidth
          label="BBOX (opcional)"
          variant="outlined"
          value={bbox}
          onChange={e => setBbox(e.target.value)}
          margin="normal"
          size="small"
        />
      </Box>
      <Box sx={{ mb: 2 }}>
        <textarea
          id="python-code"
          value={pythonCode}
          onChange={e => setPythonCode(e.target.value)}
          style={{ width: '100%', height: '300px', padding: '8px', marginTop: '10px' }}
        />
      </Box>
      <Box sx={{ textAlign: 'center', mb: 2 }}>
        <Button
          variant="outlined"
          color="primary"
          onClick={runQuery}
          disabled={!pyodide || loading}
          size="small"
          sx={{
            color: '2A3F90',
            borderColor: '#2A3F90'
          }}
        >
          {textButtton}
        </Button>
      </Box>
      {plotUrl && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="h5" gutterBottom>
            Mapa generado:
          </Typography>
          <Box
            component="img"
            src={plotUrl}
            alt="Mapa generado"
            sx={{
              maxWidth: '100%',
              maxHeight: 400,
              borderRadius: 2
            }}
          />
        </Box>
      )}
    </Box>
  );
};

export default withTracker(() => {
  const fields = { _id: 1, name: 1, url: 1, mapLocation: 1, layerSubType: 1 };
  const layersSub = Meteor.subscribe('layers.allGeoJSON', fields);
  const layerList = Layers.find({ layerSubType: 'GeoJSON' }, { fields }).fetch();
  const loadingWithTracker = !layersSub.ready() || layerList.length === 0;

  return {
    loadingWithTracker,
    layerList
  };
})(PythonComponent);

PythonComponent.defaultProps = {};

PythonComponent.propTypes = {
  layerList: PropTypes.array.isRequired,
  loadingWithTracker: PropTypes.bool.isRequired,
  pythonSettings: PropTypes.object.isRequired
};
